import { useQueryClient } from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import BankTransactionTable from "~/components/bank-transaction/bank-transaction-table";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  BankTransactionCategoriesResource,
  BankTransactionResource,
} from "~/features/bank-transaction/bank-transaction.resource";
import { getBankTransactionsPageData } from "~/features/bank-transaction/bank-transactions-page";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { withBusinessGuard } from "~/lib/route-guards";

export const Route = createFileRoute(
  "/_dashboard/dashboard/bank-transactions/",
)({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const data = await getBankTransactionsPageData({ data: { businessId } });
    // seedIfEmpty (not seed) so a fresh client-side mutation (e.g. a rename
    // that optimistically updated the list cache) isn't clobbered by stale
    // loader data when navigating back to the table.
    BankTransactionResource.seedIfEmpty(
      context.queryClient,
      { businessId, limit: 20, sortBy: "date", sortDir: "desc" },
      data.bankTransactions,
    );
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      data.businessDetail,
    );
    BankTransactionCategoriesResource.seedIfEmpty(
      context.queryClient,
      { businessId },
      data.categories,
    );
    return {
      businessId,
      bankTransactions: data.bankTransactions,
      categories: data.categories,
      activeBusiness: data.businessDetail,
    };
  },
  component: BankTransactionsPage,
});

function BankTransactionsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  BankTransactionResource.seedIfEmpty(
    qc,
    {
      businessId: data().businessId,
      limit: 20,
      sortBy: "date",
      sortDir: "desc",
    },
    data().bankTransactions,
  );
  BankTransactionCategoriesResource.seedIfEmpty(
    qc,
    { businessId: data().businessId },
    data().categories,
  );

  const businessId = useActiveBusinessId();

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Bank Transactions</h1>
          <p class="text-muted-foreground">
            Review and manage your bank transactions.
          </p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-44">
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: "/dashboard/bank-transactions/$transactionId",
                  params: { transactionId: "new" },
                })
              }
            >
              <IconPlus class="size-4" />
              Add Bank Transaction
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <BankTransactionTable businessId={businessId() ?? ""} />
    </div>
  );
}
